import { useEffect, useState } from "react";
import { Icon } from "../components/Icon.js";
import { ConfirmDialog } from "../components/ConfirmDialog.js";
import { coloringApi } from "./api.js";
import { useColoring } from "./ColoringContext.js";
import { AiButton, AutoField, useAction } from "./fields.js";
import { usePageJobs } from "./PageGeneration.js";
import { useProviderStatus } from "./providers.js";
import type { CharacterData, ColoringCharacter } from "./types.js";

const FIELDS: { key: keyof CharacterData; label: string; rows: number; placeholder?: string; wide?: boolean }[] = [
  { key: "species", label: "Species / type", rows: 0, placeholder: "Friendly ghost" },
  { key: "age", label: "Approximate age", rows: 0 },
  { key: "description", label: "Description", rows: 2, wide: true },
  { key: "body", label: "Body shape", rows: 2, placeholder: "Small round ghost with a slightly pear-shaped body" },
  { key: "face", label: "Face", rows: 2, placeholder: "Two large oval eyes and a tiny smiling mouth" },
  { key: "hair", label: "Hair / fur", rows: 2 },
  { key: "clothing", label: "Clothing", rows: 2 },
  { key: "accessories", label: "Accessories", rows: 2, placeholder: "Always wears a small knit beanie" },
  { key: "personality", label: "Personality", rows: 2 },
  { key: "distinguishing", label: "Distinguishing features", rows: 2 },
  { key: "mustHave", label: "Must-have visual traits", rows: 2, wide: true },
  { key: "mustNotChange", label: "Must-not-change traits", rows: 2, wide: true }
];

/** A character's generated reference sheet, used as a reference image on later pages. */
function CharacterSheet({ character }: { character: ColoringCharacter }) {
  const { projectId, state, reload } = useColoring();
  const { busy, run } = useAction();
  const providers = useProviderStatus();
  const { open, error } = usePageJobs(character.id);
  const noProvider = providers !== null && !providers.active;
  const usedAsRef = providers?.active?.capabilities.referenceImages ?? false;
  const mustHaveDetail = !!(character.data.description || character.data.body || character.data.face);

  const generate = () =>
    run("sheet", async () => {
      await coloringApi.characterSheet(projectId, character.id);
      await reload();
    }, "Queued the reference sheet.");

  return (
    <div className="character-sheet">
      <div className="character-sheet-art">
        {character.sheetVariantId ? (
          <img src={coloringApi.imageUrl(projectId, character.sheetVariantId)} alt={`${character.name} reference sheet`} />
        ) : (
          <div className="character-sheet-empty">
            <Icon name="image" size={26} />
            <span>No reference sheet yet</span>
          </div>
        )}
        {open && (
          <div className="page-preview-busy">
            <Icon name="clock" size={20} className="spin" />
            <span>{open.status === "queued" ? "Waiting in the queue…" : "Generating…"}</span>
          </div>
        )}
      </div>
      <div className="character-sheet-side">
        <div className="strong">Reference sheet</div>
        <p className="faint" style={{ fontSize: 12, margin: "4px 0 10px" }}>
          A clean sheet with several views of {character.name}.{" "}
          {usedAsRef
            ? "It is sent as a reference image with every page this character appears on (Balanced and Strict consistency)."
            : "The current provider can't take reference images, so the sheet is for your own reference; consistency comes from the character description."}
        </p>
        <button className="btn btn-sm btn-primary" disabled={busy !== null || !!open || noProvider || !mustHaveDetail} onClick={() => void generate()} title={!mustHaveDetail ? "Describe the character first" : undefined}>
          <Icon name={open ? "clock" : "sparkles"} size={14} className={open ? "spin" : ""} /> {character.sheetVariantId ? "Regenerate sheet" : "Generate Character Sheet"}
        </button>
        {!mustHaveDetail && <div className="faint" style={{ fontSize: 11.5, marginTop: 6 }}>Add a description, body or face first.</div>}
        {noProvider && <div className="faint" style={{ fontSize: 11.5, marginTop: 6 }}>No image provider is connected.</div>}
        {error && <div className="gen-note err" style={{ marginTop: 8 }}><Icon name="alert" size={14} /><span>{error.error}</span></div>}
        {state.book.consistency === "flexible" && (
          <div className="faint" style={{ fontSize: 11.5, marginTop: 8 }}>Consistency is set to Flexible, so sheets aren't sent with pages. Change it on the Style tab.</div>
        )}
      </div>
    </div>
  );
}

/** The Character Bible: recurring characters, optimized for visual consistency. */
export function CharactersPanel() {
  const { projectId, state, reload } = useColoring();
  const { characters } = state;
  const { busy, run } = useAction();
  const [selectedId, setSelectedId] = useState<string | null>(characters[0]?.id ?? null);
  const [confirmDelete, setConfirmDelete] = useState<ColoringCharacter | null>(null);

  useEffect(() => {
    if (!characters.some((c) => c.id === selectedId)) setSelectedId(characters[0]?.id ?? null);
  }, [characters, selectedId]);

  const selected = characters.find((c) => c.id === selectedId) ?? null;

  const add = () =>
    run("add", async () => {
      const { character } = await coloringApi.createCharacter(projectId, { name: "New character" });
      await reload();
      setSelectedId(character.id);
    });

  const suggest = () =>
    run(
      "suggest",
      async () => {
        const { created } = await coloringApi.suggestCharacters(projectId);
        await reload();
        if (created[0]) setSelectedId(created[0].id);
        return created.length;
      },
      undefined
    );

  const remove = async (c: ColoringCharacter) => {
    setConfirmDelete(null);
    await run("delete", async () => {
      await coloringApi.deleteCharacter(projectId, c.id);
      await reload();
    });
  };

  return (
    <div className="coloring-scroll">
      <div className="characters-layout">
        <aside className="characters-list">
          <div className="row gap-2" style={{ marginBottom: 12, flexWrap: "wrap" }}>
            <button className="btn btn-sm btn-primary" onClick={() => void add()} disabled={busy !== null}>
              <Icon name="plus" size={14} /> Add character
            </button>
            <AiButton busy={busy === "suggest"} onClick={() => void suggest()}>Suggest</AiButton>
          </div>
          {characters.length === 0 && (
            <p className="muted" style={{ fontSize: 13 }}>
              No recurring characters yet. If your book follows the same characters on every page, add them here so they stay consistent.
              Books of unrelated subjects can skip this.
            </p>
          )}
          {characters.map((c) => (
            <button key={c.id} className={`character-card ${c.id === selectedId ? "on" : ""}`} onClick={() => setSelectedId(c.id)}>
              <div className="character-thumb">
                {c.sheetVariantId ? (
                  <img src={coloringApi.imageUrl(projectId, c.sheetVariantId)} alt={`${c.name} reference sheet`} />
                ) : (
                  <Icon name="users" size={20} style={{ color: "var(--text-faint)" }} />
                )}
              </div>
              <div className="col" style={{ minWidth: 0 }}>
                <span className="strong truncate">{c.name}</span>
                <span className="faint truncate" style={{ fontSize: 11.5 }}>{c.data.species || "No type yet"}</span>
              </div>
            </button>
          ))}
        </aside>

        <section className="card pad characters-editor">
          {!selected ? (
            <p className="muted" style={{ margin: 0 }}>Select or add a character to edit.</p>
          ) : (
            <>
              <div className="row gap-2" style={{ marginBottom: 14 }}>
                <div className="grow">
                  <AutoField
                    label="Name"
                    value={selected.name}
                    onSave={async (name) => {
                      if (!name.trim()) return;
                      await coloringApi.updateCharacter(projectId, selected.id, { name: name.trim() });
                      await reload();
                    }}
                  />
                </div>
                <button className="btn btn-sm btn-ghost" onClick={() => setConfirmDelete(selected)} title="Delete character">
                  <Icon name="trash" size={14} /> Delete
                </button>
              </div>
              <CharacterSheet character={selected} />
              <div className="coloring-fields">
                {FIELDS.map((f) => (
                  <div key={`${selected.id}-${f.key}`} className={f.wide ? "span-2" : ""}>
                    <AutoField
                      label={f.label}
                      rows={f.rows}
                      placeholder={f.placeholder}
                      value={selected.data[f.key]}
                      onSave={async (v) => {
                        await coloringApi.updateCharacter(projectId, selected.id, { data: { [f.key]: v } });
                        await reload();
                      }}
                    />
                  </div>
                ))}
              </div>
            </>
          )}
        </section>
      </div>
      {confirmDelete && (
        <ConfirmDialog
          title={`Delete ${confirmDelete.name}?`}
          message="The character is removed from the book and from any page that uses them. Existing artwork is not deleted."
          confirmLabel="Delete"
          danger
          onConfirm={() => void remove(confirmDelete)}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
}
